import { useEffect, useState } from "react";
import axios from "axios";
import { useAuth, API } from "@/lib/auth";
import { useBranding } from "@/lib/branding";
import { isMsalConfigured, isInsideIframe } from "@/lib/msalInstance";

export default function SignIn() {
  const { signIn, status, msalActive } = useAuth();
  const { branding, host, primaryLogoSrc } = useBranding();
  const [users, setUsers] = useState([]);
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState("");
  const iframed = isInsideIframe();
  const showMicrosoftButton = isMsalConfigured() && !iframed;
  const showDevSignIn = !msalActive;
  const displayName = branding?.identity?.display_name || "Coolman Fuels";
  const tagline = branding?.identity?.tagline
    || host?.subtitle
    || `Sign in with your ${displayName} account`;

  useEffect(() => {
    if (msalActive) return; // dev-list only fetched in dev-mode
    axios
      .get(`${API}/auth/me`, { headers: { "X-User-Email": "kevin@coolmanfuels.ca" } })
      .then(() =>
        axios.get(`${API}/users`, { headers: { "X-User-Email": "kevin@coolmanfuels.ca" } }),
      )
      .then((r) => setUsers(r.data))
      .catch(() => {});
  }, [msalActive]);

  const handleMicrosoftSignIn = async () => {
    setError("");
    setSigningIn(true);
    try {
      await signIn();
    } catch (e) {
      setError(e?.errorMessage || e?.message || "Sign-in failed. Please try again.");
    } finally {
      setSigningIn(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#f3f4f6] px-4">
      <div className="w-full max-w-md border border-gray-200 bg-white p-8 shadow-sm">
        <div className="mb-6 flex flex-col items-center text-center">
          <img src={primaryLogoSrc()} alt={displayName} className="h-20 w-auto" data-testid="signin-logo" />
          <h1 className="mt-4 text-2xl font-semibold text-gray-900" data-testid="signin-app-title">{host?.name || "Fuel Order Centre"}</h1>
          <p className="mt-1.5 text-[15px] text-gray-600" data-testid="signin-tagline">{tagline}</p>
        </div>

        {showMicrosoftButton ? (
          <button
            data-testid="signin-microsoft-button"
            disabled={signingIn}
            className="flex w-full items-center justify-center gap-3 rounded-md border border-gray-300 bg-white px-4 py-3 text-[15px] font-semibold text-gray-900 hover:bg-gray-50 disabled:opacity-60"
            onClick={handleMicrosoftSignIn}
          >
            <svg width="20" height="20" viewBox="0 0 23 23">
              <rect x="1" y="1" width="10" height="10" fill="#f25022" />
              <rect x="12" y="1" width="10" height="10" fill="#7fba00" />
              <rect x="1" y="12" width="10" height="10" fill="#00a4ef" />
              <rect x="12" y="12" width="10" height="10" fill="#ffb900" />
            </svg>
            {signingIn ? "Signing in…" : "Sign in with Microsoft"}
          </button>
        ) : iframed && isMsalConfigured() ? (
          <div
            data-testid="signin-iframe-notice"
            className="rounded-md border border-blue-200 bg-blue-50/60 p-4 text-sm text-blue-900"
          >
            <div className="mb-2 font-semibold">Preview mode</div>
            <p className="mb-3 text-blue-800">
              Microsoft sign-in cannot run inside the Emergent preview iframe. Use one of the developer sign-in buttons below, or open the app in a new tab to test real SSO.
            </p>
            <a
              data-testid="signin-open-new-tab"
              href={window.location.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 rounded-md border border-blue-300 bg-white px-3 py-1.5 text-xs font-semibold text-blue-800 hover:bg-blue-50"
            >
              Open in a new tab →
            </a>
          </div>
        ) : null}

        {error && (
          <div
            data-testid="signin-error"
            className="mt-4 rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-800"
          >
            {error}
          </div>
        )}

        {status === "denied" && (
          <div
            data-testid="signin-denied"
            className="mt-5 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
          >
            <strong>Access not provisioned.</strong> Your Microsoft account is valid but you have not been granted access to Fuel Order Centre. Please contact Sean.
          </div>
        )}

        {showDevSignIn && (
          <div className="mt-8 border-t border-gray-200 pt-5">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-gray-500">
              Development sign-in
            </div>
            <p className="mb-3 text-sm text-gray-500">
              For testing only. Disables automatically once Microsoft SSO is configured.
            </p>
            <div className="grid grid-cols-1 gap-2">
              {users.length === 0 ? (
                <>
                  <DevButton email="kevin@coolmanfuels.ca" label="Kevin Coolman · Owner" signIn={signIn} />
                  <DevButton email="sean@coolmanfuels.ca" label="Sean Wheatley · Administrator" signIn={signIn} />
                  <DevButton email="nathan@coolmanfuels.ca" label="Nathan King · Office Staff" signIn={signIn} />
                  <DevButton email="wes@coolmanfuels.ca" label="Wes Brown · Driver" signIn={signIn} />
                  <DevButton email="dennis@coolmanfuels.ca" label="Dennis Pym · Driver" signIn={signIn} />
                  <DevButton email="garrett@coolmanfuels.ca" label="Garrett Wolf · Driver" signIn={signIn} />
                </>
              ) : (
                users.map((u) => (
                  <DevButton key={u.id} email={u.email} label={`${u.full_name} · ${u.role}`} signIn={signIn} />
                ))
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DevButton({ email, label, signIn }) {
  return (
    <button
      data-testid={`dev-signin-${email}`}
      onClick={() => {
        localStorage.setItem("foc.userEmail", email);
        window.location.assign("/dashboard");
      }}
      className="rounded-md border border-gray-300 bg-white px-3 py-2 text-left text-sm text-gray-800 hover:bg-gray-50"
    >
      {label}
    </button>
  );
}
